import React, { useEffect, useCallback, useState } from 'react';
import {
  Navigate, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import LearningPathway from 'src/components/learning-pathway-v2';
import { AssessmentOverlay } from 'src/components/credential/AssessmentOverlay';
import { CredentialCompletionPanel } from 'src/components/credential/CredentialCompletionPanel';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import { useBundle } from '@amzn/react-arb-tools';
import { MessageBundle } from '@amzn/arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { useAppSelector } from 'src/redux/hooks';
import { useGetCustomerCredentialQuery } from 'src/redux/slices/api/su';
import { selectLearningPathwayById } from 'src/redux/slices/learningPathways';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import { selectAllModulesComplete, selectCompletedModuleCount } from 'src/redux/slices/learningModules';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { AppState } from 'src/redux/store';
import ClientLogger from 'src/utils/logger';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { removeSearchParam } from 'src/utils/searchParams';
import { getStatusCodeFromError } from 'src/utils/error';
import CredentialThumbnail from 'src/assets/su-loading-illustration.png';

const ASSESSMENT_QUESTION_COUNT = 10;

// Greyed placeholder shown before the credential is earned. This is a generic, non-credential
// -specific asset (it carries no credential identity), so it is safe as a shared constant.
const UNEARNED_BADGE_SRC = 'https://d1dspppqvg0rnv.cloudfront.net/assets/e6aa1925-6d78-4b43-802d-7c44d877f91c/original.png';

export const buildAssessmentOverlayCopy = (credentialBundle: MessageBundle) => ({
  title: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_TITLE),
  questionProgressLabel: (current: number, total: number) => t(
    credentialBundle,
    CREDENTIAL_STRING_ID.ASSESSMENT_QUESTION_COUNTER,
    { current, total },
  ),
  backLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_BACK),
  continueLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_CONTINUE),
  submitLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_SUBMIT),
  closeLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_CLOSE),
  retakeLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_RETAKE),
  downloadCertificationLabel: t(credentialBundle, CREDENTIAL_STRING_ID.DOWNLOAD_CERTIFICATE_CTA),
  passedTitle: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_PASSED_TITLE),
  failedTitle: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_FAILED_TITLE),
  scoreLabel: (scorePercent: number) => t(
    credentialBundle,
    CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_SCORE_PERCENT,
    { scorePercent },
  ),
  passedBody: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_PASSED_BODY),
  failedBody: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_FAILED_BODY),
  // SUOS sends the exact credential-bundle key as each question prompt / option value (LP-style
  // direct match), so we resolve it straight against the bundle via t(), exactly like the sibling
  // copy fields above and pageHeaderV2. After the SUOS config fix the backend always sends valid
  // bundle keys, so a miss is a genuine misconfiguration that t() rightly logs (TranslationNotFound)
  // while echoing the id back.
  resolveQuestionText: (promptId: string) => t(credentialBundle, promptId),
  resolveOptionText: (textId: string) => t(credentialBundle, textId),
});

const CredentialPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);
  const [searchParams] = useSearchParams();
  const { credentialId } = useParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const { viewMetric } = useMetrics();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isAssessmentEarned, setIsAssessmentEarned] = useState(false);
  const isCredentialsPilotT1 = useAppSelector(
    (state: AppState) => selectIsWeblabInT1(state, WeblabId.CredentialsPilotLaunch),
  );

  const {
    data: customerCredentialData,
    isLoading,
    isError,
    error,
    isUninitialized,
    refetch,
  } = useGetCustomerCredentialQuery(
    { credentialId: credentialId ?? '', clientId, selLocale },
    { skip: !isCredentialsPilotT1 || !credentialId },
  );
  const credential = customerCredentialData?.customerCredential?.credential;
  const learningPathway = credential?.learningPathway;
  const learningPathwayId = learningPathway?.learningPathwayId;
  const hasAward = Boolean(customerCredentialData?.customerCredential?.award);
  // Earned state is server-authoritative: it is driven by the persisted `award` from
  // getCustomerCredential (refetched on assessment pass). `isAssessmentEarned` is a transitional
  // stopgap for the M1 slim backend, which may not return the award immediately after submit, so
  // the seller's just-earned credential still unlocks. Remove once the backend returns the award
  // reliably on refetch.
  const isCredentialEarned = hasAward || isAssessmentEarned;

  // Badge and certificate assets come from the credential definition. When the (M1 slim) model
  // omits them, they are left undefined so the UI hides the earned badge / certificate download
  // rather than substituting another credential's asset.
  const earnedBadgeSrc = credential?.badge?.imageUrl;
  const certificateUrl = credential?.certificateUrl;
  const assessmentQuestionCount = credential?.assessment?.questionCount ?? ASSESSMENT_QUESTION_COUNT;

  // The seller has an in-flight attempt when the customer assessment reports IN_PROGRESS. A
  // not-started assessment carries no state (attemptsUsed 0), so it stays in the todo state.
  const isAssessmentInProgress = customerCredentialData?.customerCredential?.assessment?.state === 'IN_PROGRESS'
    && !isCredentialEarned;

  // The page also renders ErrorView for two non-query conditions (missing credentialId, and a
  // successful fetch with no learningPathwayId). Fold them into the fault signal so page
  // performance counts them as faults rather than successful loads.
  const hasRenderError = !credentialId || (!isLoading && !isUninitialized && !learningPathwayId);

  // Non-pilot sellers are redirected below and the credential query is skipped, so there is no
  // real page load to measure; keep the hook in its not-loaded state so it never emits for them.
  usePagePerformance(
    FeatureName.CredentialPage,
    isLoading || !isCredentialsPilotT1,
    isError || hasRenderError,
    credentialId,
  );

  const pathway = useAppSelector(
    (state: AppState) => selectLearningPathwayById(state, learningPathwayId ?? ''),
  );
  const moduleIds = pathway?.data?.sections?.reduce<string[]>(
    (acc, section) => acc.concat(section.learningModuleIds),
    [],
  ) ?? [];
  const isAssessmentUnlocked = useAppSelector(
    (state: AppState) => selectAllModulesComplete(state, moduleIds),
  );
  const completedModuleCount = useAppSelector(
    (state: AppState) => selectCompletedModuleCount(state, moduleIds),
  );
  // Credential progress spans all modules PLUS the assessment, so the assessment is one extra
  // step in the denominator (3 modules = 75%, assessment earned = 100%). The assessment step is
  // earned when the seller passes the scored assessment.
  const totalSteps = moduleIds.length + 1;
  const completedSteps = completedModuleCount + (isCredentialEarned ? 1 : 0);
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  const learningModuleInPathway = learningPathway?.sections?.some(
    (section) => section.moduleList?.some((module) => module.moduleId === learningModuleId),
  );

  useEffect(() => {
    if (isCredentialsPilotT1 && !isLoading && !isError && !isUninitialized && learningPathwayId) {
      viewMetric({
        feature: FeatureName.CredentialDetail,
        metadata: {
          credentialId,
          pathwayId: learningPathwayId,
          clientId,
          path: location.pathname,
        },
      });
    }
  }, [isCredentialsPilotT1, isLoading, isError, isUninitialized, learningPathwayId, viewMetric, credentialId, clientId, location.pathname]);

  useEffect(() => {
    if (!isLoading && !isError && !isUninitialized && !learningPathwayId) {
      ClientLogger.error(ClientLog.PayloadProcessingError, {
        message: 'Learning pathway data unavailable after successful fetch',
        credentialId,
        learningPathwayId,
        clientId,
        url: window.location.href,
      });
    }
  }, [isLoading, isError, isUninitialized, learningPathwayId, credentialId, clientId]);

  useEffect(() => {
    if (!isError) {
      return;
    }
    const statusCode = getStatusCodeFromError(error);
    const logContext = {
      credentialId,
      learningPathwayId,
      clientId,
      path: location.pathname,
      url: window.location.href,
    };
    if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
      ClientLogger.warn(ClientLog.IllegalInternalDataState, logContext);
    } else {
      ClientLogger.error(ClientLog.IllegalInternalDataState, logContext);
    }
  }, [isError, error, credentialId, learningPathwayId, clientId, location.pathname]);

  useEffect(() => {
    if (!credentialId) {
      ClientLogger.error(ClientLog.IllegalInternalDataState, {
        message: `Expected credentialId to be present in browser URL path: ${location.pathname}`,
        path: location.pathname,
        url: window.location.href,
      });
    }
  }, [credentialId, location.pathname]);

  const appendModuleSearchParam = (moduleId: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, moduleId);
    navigate({ search: updatedSearchParams.toString() }, { replace: true });
  };

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  if (!isCredentialsPilotT1) {
    return <Navigate to={ROUTES.NEW_LEARN.BASE} replace />;
  }

  if (!credentialId) {
    return <ErrorView />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  if (isError) {
    return <ErrorView statusCode={getStatusCodeFromError(error)} />;
  }

  if (!learningPathwayId) {
    return <ErrorView />;
  }

  const resolvedLearningPathwayId = learningPathwayId;

  return (
    <div>
      <LearningPathway
        learningPathwayId={resolvedLearningPathwayId}
        onModuleSelect={appendModuleSearchParam}
        progress={{
          percentage: completionPercentage,
          badge: earnedBadgeSrc
            ? {
              earned: isCredentialEarned,
              earnedSrc: earnedBadgeSrc,
              unearnedSrc: UNEARNED_BADGE_SRC,
              alt: t(credentialBundle, CREDENTIAL_STRING_ID.BADGE_ALT),
            }
            : undefined,
        }}
        hideSectionLabels
        enforceSerialProgression
        assessment={{
          title: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_TITLE),
          description: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_DESCRIPTION),
          imageSrc: CredentialThumbnail,
          ctaLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_START_CTA),
          questionCount: assessmentQuestionCount,
          locked: !isAssessmentUnlocked,
          earned: isCredentialEarned,
          inProgress: isAssessmentInProgress,
          onStart: () => setIsAssessmentModalOpen(true),
        }}
      />
      {isCredentialEarned && certificateUrl && (
        <CredentialCompletionPanel
          message={t(credentialBundle, CREDENTIAL_STRING_ID.COMPLETION_PANEL_MESSAGE)}
          downloadLabel={t(credentialBundle, CREDENTIAL_STRING_ID.DOWNLOAD_CERTIFICATE_CTA)}
          certificateUrl={certificateUrl}
        />
      )}
      {isAssessmentModalOpen && (
        <AssessmentOverlay
          credentialId={credentialId}
          clientId={clientId}
          selLocale={selLocale}
          copy={buildAssessmentOverlayCopy(credentialBundle)}
          completionCertificateUrl={certificateUrl}
          onClose={() => setIsAssessmentModalOpen(false)}
          onPassed={() => {
            setIsAssessmentEarned(true);
            // Refetch so earned state is confirmed by the server-persisted award rather than the
            // optimistic local flag (the M1 stopgap above).
            refetch();
            viewMetric({
              feature: FeatureName.CredentialEarned,
              metadata: { credentialId, clientId, path: location.pathname },
            });
          }}
        />
      )}
      {learningModuleId && learningModuleInPathway && (
        <LearningModuleModal
          learningModuleId={learningModuleId}
          onModalClose={removeModuleSearchParam}
          parentId={resolvedLearningPathwayId}
          parentType={EntityType.LearningPathway}
        />
      )}
    </div>
  );
};

export default CredentialPage;
